import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, AlertTriangle, Users, Plus, IndianRupee, HardHat, Building2 } from 'lucide-react';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import PaginationComponent from '../../../shared/components/PaginationComponent';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import Modal from '../../../shared/components/ui/Modal';
import { canManageLabourWages } from '../../../shared/utils/workforceAccess';
import { newIdempotencyKey } from '../../../shared/utils/idempotency';
import {
  getLabourRegister,
  getSafetyOutstanding,
  recordLabourPaymentBulk
} from '../../services/api/implementation/impl';
import {
  StatusPill,
  Tile,
  toneForStatus
} from '../../../shared/components/ui/primitives';

const LABOUR_TYPES = [
  { value: '', label: 'All types' },
  { value: 'IN_HOUSE', label: 'In-house' },
  { value: 'CONTRACTOR', label: 'Contractor' }
];

const SKILL_LEVELS = [
  { value: '', label: 'All skills' },
  { value: 'SKILLED', label: 'Skilled' },
  { value: 'SEMI_SKILLED', label: 'Semi-skilled' },
  { value: 'UNSKILLED', label: 'Unskilled' }
];

/**
 * Company-wide labour register.
 *
 * The workforce has existed in the database for a long time but was only ever
 * reachable through a contractor's own login, so this is the first place a
 * builder can see who is on their sites.
 *
 * The statutory column is the reason this screen matters beyond a list: the
 * CLRA register of workmen cannot be produced for anyone missing father's name,
 * date of birth, permanent address and the rest, and those fields are not
 * collected on site because a supervisor adding labour at 7am does not have
 * them. This is where somebody fills the gaps, so the gaps have to be visible.
 */
const LabourRegister = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=labour' });
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [labourType, setLabourType] = useState('');
  const [skillLevel, setSkillLevel] = useState('');
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  // Off by default — the roster is who turns up. But somebody taken off it has
  // to be findable, or reactivating him means editing the database.
  const [includeInactive, setIncludeInactive] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [payOpen, setPayOpen] = useState(false);

  const queryClient = useQueryClient();
  const canRecord = canManageLabourWages(
    Cookies.get('userDesignation'),
    Cookies.get('userVertical')
  );

  const payBulk = useMutation({
    mutationFn: recordLabourPaymentBulk,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['labourRegister'] });
      queryClient.invalidateQueries({ queryKey: ['labourDues'] });
      setPayOpen(false);
      setSelected(new Set());

      // Named failures, not a count. A settlement that quietly missed four
      // people is the thing somebody has to be able to act on straight away.
      const failed = response?.data?.failed ?? [];
      if (failed.length) {
        toast.error(
          `${response?.meta?.succeeded ?? 0} paid. Not paid: ${failed
            .map((f) => f.name || `#${f.worker_id}`)
            .join(', ')}`,
          { duration: 8000 }
        );
      } else {
        toast.success(response?.message || 'Payments recorded');
      }
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not record the payments')
  });

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const limit = 25;

  // Fit / unfit at a glance. It was only on each workman's own page, so
  // nobody looking down the roster could see who should not start today. One
  // call returns every active workman's verdict.
  const safety = useQuery({
    queryKey: ['labourSafetyOutstanding'],
    queryFn: getSafetyOutstanding,
    staleTime: 60 * 1000
  });
  const safetyById = useMemo(() => {
    const rows = safety.data?.data ?? [];
    return new Map(rows.map((r) => [r.worker_id, r]));
  }, [safety.data]);

  const { data, isFetching } = useQuery({
    queryKey: [
      'labourRegister',
      page,
      search,
      labourType,
      skillLevel,
      includeInactive
    ],
    queryFn: () =>
      getLabourRegister({
        page,
        limit,
        search,
        labour_type: labourType,
        skill_level: skillLevel,
        include_inactive: includeInactive ? 'true' : undefined
      }),
    keepPreviousData: true
  });

  const rows = useMemo(() => {
    const all = data?.data ?? [];
    return onlyIncomplete
      ? all.filter((w) => (w.missing_statutory_fields ?? []).length > 0)
      : all;
  }, [data, onlyIncomplete]);

  const meta = data?.meta ?? {};
  const totalPages = meta.total ? Math.ceil(meta.total / limit) : 1;

  const incompleteCount = useMemo(
    () =>
      (data?.data ?? []).filter((w) => (w.missing_statutory_fields ?? []).length > 0)
        .length,
    [data]
  );

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Labour Register' }} />

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Tile variant="card"
            label="Workforce"
            value={meta.workforce_count ?? 0}
            icon={<Users size={18} />}
          />
          <Tile variant="card"
            label="In-house"
            value={meta.in_house_count ?? 0}
            icon={<HardHat size={18} />}
            tone="success"
          />
          <Tile variant="card"
            label="Contractor"
            value={meta.contractor_count ?? 0}
            icon={<Building2 size={18} />}
            tone="info"
          />
        </div>

        {incompleteCount > 0 && (
          <button
            type="button"
            onClick={() => setOnlyIncomplete((v) => !v)}
            className={`flex items-center gap-2 w-full text-left rounded-control border px-3 py-2 text-xs transition ${
              onlyIncomplete
                ? 'border-warning-border bg-warning-bg text-warning-fg'
                : 'border-warning-border bg-warning-bg text-warning-fg hover:bg-warning-bg'
            }`}
          >
            <AlertTriangle size={16} />
            <span>
              {incompleteCount} workman{incompleteCount === 1 ? '' : 'en'} on this page
              cannot appear on the statutory register yet.
            </span>
            <span className="ml-auto font-semibold underline">
              {onlyIncomplete ? 'Show all' : 'Show only these'}
            </span>
          </button>
        )}

        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by name"
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm"
            />
          </div>

          <Select
            value={labourType}
            onChange={(v) => {
              setLabourType(v);
              setPage(1);
            }}
            options={LABOUR_TYPES}
          />
          <Select
            value={skillLevel}
            onChange={(v) => {
              setSkillLevel(v);
              setPage(1);
            }}
            options={SKILL_LEVELS}
          />

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-brand-700"
              checked={includeInactive}
              onChange={(e) => {
                setIncludeInactive(e.target.checked);
                setPage(1);
              }}
            />
            Include inactive
          </label>

          <button
            type="button"
            onClick={() => navigate('/builder/operations/labour/new')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-700 text-white text-sm"
          >
            <Plus size={16} />
            Add labour
          </button>
        </div>

        {canRecord && selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-brand-100 bg-brand-50 px-4 py-2.5">
            <span className="text-sm font-medium text-brand-900">
              {selected.size} selected
            </span>
            <button
              type="button"
              onClick={() => setPayOpen(true)}
              className="flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-1.5 text-xs font-medium text-white"
            >
              <IndianRupee size={13} /> Record payments
            </button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="text-xs text-brand-800 hover:underline"
            >
              Clear
            </button>
          </div>
        )}

        {isFetching ? (
          <Loader />
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {canRecord && (
                    <Th className="w-8">
                      <input
                        type="checkbox"
                        aria-label="Select all on this page"
                        className="h-3.5 w-3.5 accent-brand-700"
                        checked={rows.length > 0 && selected.size === rows.length}
                        onChange={(e) =>
                          setSelected(
                            e.target.checked ? new Set(rows.map((r) => r.id)) : new Set()
                          )
                        }
                      />
                    </Th>
                  )}
                  <Th>Name</Th>
                  <Th>Type</Th>
                  <Th>Trade / Skill</Th>
                  <Th>Rate/day</Th>
                  <Th>Projects</Th>
                  <Th>Today</Th>
                  <Th>Safety</Th>
                  <Th>Register</Th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={canRecord ? 9 : 8} className="text-center py-10 text-gray-500">
                      No labour found.
                    </td>
                  </tr>
                )}
                {rows.map((worker) => {
                  const missing = worker.missing_statutory_fields ?? [];
                  return (
                    <tr
                      key={worker.id}
                      onClick={() => navigate(`/builder/operations/labour/${worker.id}`)}
                      className="border-t border-gray-100 cursor-pointer hover:bg-gray-50"
                    >
                      {canRecord && (
                        <Td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            aria-label={`Select ${worker.name}`}
                            className="h-3.5 w-3.5 accent-brand-700"
                            checked={selected.has(worker.id)}
                            onChange={() => toggle(worker.id)}
                          />
                        </Td>
                      )}
                      <Td>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{worker.name}</span>
                          {worker.is_active === false && (
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                              Inactive
                            </span>
                          )}
                        </div>
                        {worker.sp?.company_name && (
                          <div className="text-xs text-gray-500">
                            {worker.sp.company_name}
                          </div>
                        )}
                      </Td>
                      <Td>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            worker.labour_type === 'IN_HOUSE'
                              ? 'bg-brand-50 text-brand-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {worker.labour_type === 'IN_HOUSE' ? 'In-house' : 'Contractor'}
                        </span>
                      </Td>
                      <Td>{[worker.trade, worker.skill_level].filter(Boolean).join(' · ') || '—'}</Td>
                      <Td>{worker.ratePerDay ? `₹${Number(worker.ratePerDay)}` : '—'}</Td>
                      <Td>
                        {(worker.workerProjectMappings ?? [])
                          .map((m) => m.project?.name)
                          .filter(Boolean)
                          .join(', ') || '—'}
                      </Td>
                      <Td>
                        <MusterPill status={worker.today_status} />
                      </Td>
                      <Td>
                        <SafetyPill verdict={safetyById.get(worker.id)} />
                      </Td>
                      <Td>
                        {missing.length === 0 ? (
                          <span className="text-xs text-success-fg">Complete</span>
                        ) : (
                          <span
                            className="text-xs text-warning-fg"
                            title={`Missing: ${missing.join(', ')}`}
                          >
                            {missing.length} field{missing.length === 1 ? '' : 's'} missing
                          </span>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && !onlyIncomplete && (
          <PaginationComponent
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        )}
      </div>

      {payOpen && (
        <BulkPayModal
          workers={rows.filter((r) => selected.has(r.id))}
          busy={payBulk.isPending}
          onClose={() => setPayOpen(false)}
          onSubmit={(values) => payBulk.mutate(values)}
        />
      )}
    </Layout>
  );
};


const Select = ({ value, onChange, options }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
);

/** A workman's day on the muster. */
const MusterPill = ({ status }) => {
  const LABELS = {
    PRESENT: 'Present',
    ABSENT: 'Absent',
    HALF_DAY: 'Half day',
    OT_ONLY: 'Overtime'
  };
  return (
    <StatusPill dense tone={toneForStatus(status)}>
      {LABELS[status] ?? 'Not marked'}
    </StatusPill>
  );
};

/** Site ready / Action required / Not cleared, with the reason on hover. */
const SAFETY_LOOK = {
  SITE_READY: { label: 'Site ready', cls: 'bg-success-bg text-success-fg' },
  ACTION_REQUIRED: { label: 'Action required', cls: 'bg-warning-bg text-warning-fg' },
  NOT_CLEARED: { label: 'Not cleared', cls: 'bg-danger-bg text-danger-fg' }
};

const SafetyPill = ({ verdict }) => {
  if (!verdict) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  const look = SAFETY_LOOK[verdict.status] ?? SAFETY_LOOK.NOT_CLEARED;
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${look.cls}`}
      title={(verdict.reasons ?? []).join('\n')}
    >
      {look.label}
    </span>
  );
};

const Th = ({ children, className = '' }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left font-medium ${className}`}>
    {children}
  </th>
);
const Td = ({ children, onClick }) => (
  <td className="px-4 py-3 align-top" onClick={onClick}>
    {children}
  </td>
);

/**
 * Settle several workmen at once.
 *
 * One amount each, entered per person rather than split from a total: a group
 * settled on a Saturday are owed different sums, and dividing a lump equally is
 * how somebody ends up short.
 *
 * The server does not wrap these in a transaction, and the response names who
 * was paid and who was not — a partially completed payout that says so is
 * recoverable, one that rolls back after the cash left the office is not.
 */
const BulkPayModal = ({ workers, busy, onClose, onSubmit }) => {
  const [amounts, setAmounts] = useState({});
  const [mode, setMode] = useState('CASH');
  const [remarks, setRemarks] = useState('');

  // One key PER WORKMAN, fixed when the modal opens. A batch that half applied
  // and was retried then skips the rows that landed and writes the ones that
  // did not — a single key for the whole batch could not express that.
  const [keys] = useState(() =>
    Object.fromEntries(workers.map((w) => [w.id, newIdempotencyKey('pay')]))
  );

  const entered = workers
    .map((w) => ({ worker_id: w.id, name: w.name, amount: Number(amounts[w.id]) }))
    .filter((r) => Number.isFinite(r.amount) && r.amount > 0);

  const total = entered.reduce((sum, r) => sum + r.amount, 0);

  return (
    <Modal name={`Pay ${workers.length} workmen`} onClose={onClose} width="560px">
      <div className="space-y-3">
        <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
          {workers.map((worker) => (
            <div key={worker.id} className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-gray-800">{worker.name}</div>
                <div className="text-xs text-gray-500">
                  {worker.sp?.company_name ?? 'In-house'}
                </div>
              </div>
              <input
                type="number"
                inputMode="decimal"
                placeholder="0.00"
                value={amounts[worker.id] ?? ''}
                onChange={(e) =>
                  setAmounts((a) => ({ ...a, [worker.id]: e.target.value }))
                }
                className="w-32 rounded-lg border border-gray-300 px-3 py-1.5 text-right text-sm tabular-nums"
              />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-600">Mode</span>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              {['CASH', 'BANK_TRANSFER', 'UPI', 'CHEQUE'].map((m) => (
                <option key={m} value={m}>
                  {m.charAt(0) + m.slice(1).toLowerCase().replace('_', ' ')}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-600">Remarks</span>
            <input
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
        </div>

        <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm">
          <span className="text-gray-600">
            {entered.length} of {workers.length} have an amount
          </span>
          <span className="font-semibold tabular-nums text-gray-900">
            ₹{total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </span>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || entered.length === 0}
            onClick={() =>
              onSubmit({
                payments: entered.map(({ worker_id, amount }) => ({
                  worker_id,
                  amount,
                  client_payment_uuid: keys[worker_id]
                })),
                mode,
                remarks: remarks || undefined
              })
            }
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy ? 'Recording…' : `Pay ${entered.length}`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LabourRegister;
